import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { EmbedGate } from "@/components/EmbedGate";
import { CanonicalAndRobots } from "@/components/CanonicalAndRobots";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { IframeHeightReporter } from "@/components/IframeHeightReporter";
import { ConfirmDialogProvider } from "@/components/ConfirmDialog";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { AuthProvider } from "@/lib/auth-context";

import AccessIndex from "@/routes/access.index";
import AccessLogin from "@/routes/access.login";
import AccessSkapaKonto from "@/routes/access.skapa-konto";
import AccessBekraftaMejl from "@/routes/access.bekrafta-mejl";
import AccessGlomtLosenord from "@/routes/access.glomt-losenord";
import AccessResetPassword from "@/routes/access.reset-password";
import AccessAktivera from "@/routes/access.aktivera";
import AccessValkommen from "@/routes/access.valkommen";
import AccessDashboard from "@/routes/access.dashboard";
import AccessFormaner from "@/routes/access.formaner";
import AccessErbjudanden from "@/routes/access.erbjudanden";
import AccessEvent from "@/routes/access.event";
import AccessProfil from "@/routes/access.profil";
import AccessBjudIn from "@/routes/access.bjud-in";

import AccessVillkor from "@/routes/access.villkor";
import AccessIntegritetspolicy from "@/routes/access.integritetspolicy";
import AccessAngerratt from "@/routes/access.angerratt";

import { CookieBanner } from "@/components/CookieBanner";

import AfPortalIndex from "@/routes/af-portal.index";
import AfPortalDashboard from "@/routes/af-portal.dashboard";
import AfPortalKunder from "@/routes/af-portal.kunder";
import AfPortalPartners from "@/routes/af-portal.partners";
import AfPortalEvent from "@/routes/af-portal.event";

import AfPortalSms from "@/routes/af-portal.sms";
import AfPortalFormaner from "@/routes/af-portal.formaner";
import AfPortalMejl from "@/routes/af-portal.mejl";
import AfPortalUtskick from "@/routes/af-portal.utskick";
import AfPortalPersonal from "@/routes/af-portal.personal";
import AfPortalRapporter from "@/routes/af-portal.rapporter";
import AfPortalLosaIn from "@/routes/af-portal.losa-in";

const queryClient = new QueryClient();

function NotFound() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Sidan kunde inte hittas</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Sidan du letar efter finns inte eller har flyttats.
        </p>
        <div className="mt-6">
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Till startsidan
          </a>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <BrowserRouter>
        <EmbedGate>
          <AuthProvider>
            <ConfirmDialogProvider>
            <PaymentTestModeBanner />
            <IframeHeightReporter />
            <CanonicalAndRobots />
            <Routes>
              <Route path="/" element={<Navigate to="/access" replace />} />

              <Route path="/access" element={<AccessIndex />} />
              <Route path="/access/login" element={<AccessLogin />} />
              <Route path="/access/skapa-konto" element={<AccessSkapaKonto />} />
              <Route path="/access/bekrafta-mejl" element={<AccessBekraftaMejl />} />
              <Route path="/access/glomt-losenord" element={<AccessGlomtLosenord />} />
              <Route path="/access/reset-password" element={<AccessResetPassword />} />
              <Route path="/access/aktivera" element={<AccessAktivera />} />
              <Route path="/access/valkommen" element={<AccessValkommen />} />
              <Route path="/access/dashboard" element={<AccessDashboard />} />
              <Route path="/access/formaner" element={<AccessFormaner />} />
              <Route path="/access/erbjudanden" element={<AccessErbjudanden />} />
              <Route path="/access/event" element={<AccessEvent />} />
              <Route path="/access/profil" element={<AccessProfil />} />
              <Route path="/access/bjud-in" element={<AccessBjudIn />} />
              <Route path="/access/om-dig" element={<Navigate to="/access/profil" replace />} />
              <Route path="/access/villkor" element={<AccessVillkor />} />
              <Route path="/access/integritetspolicy" element={<AccessIntegritetspolicy />} />
              <Route path="/access/angerratt" element={<AccessAngerratt />} />

              <Route path="/af-portal" element={<AfPortalIndex />} />
              <Route path="/af-portal/dashboard" element={<AfPortalDashboard />} />
              <Route path="/af-portal/kunder" element={<AfPortalKunder />} />
              <Route path="/af-portal/partners" element={<AfPortalPartners />} />
              <Route path="/af-portal/event" element={<AfPortalEvent />} />
              <Route path="/af-portal/registrera" element={<Navigate to="/af-portal/kunder" replace />} />
              <Route path="/af-portal/losa-in" element={<AfPortalLosaIn />} />
              <Route path="/af-portal/utskick" element={<AfPortalUtskick />} />
              <Route path="/af-portal/sms" element={<Navigate to="/af-portal/utskick" replace />} />
              <Route path="/af-portal/mejl" element={<Navigate to="/af-portal/utskick" replace />} />
              <Route path="/af-portal/admin" element={<Navigate to="/af-portal/formaner" replace />} />
              <Route path="/af-portal/formaner" element={<AfPortalFormaner />} />
              <Route path="/af-portal/personal" element={<AfPortalPersonal />} />
              <Route path="/af-portal/rapporter" element={<AfPortalRapporter />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
            <CookieBanner />
            </ConfirmDialogProvider>
            </AuthProvider>
          
        </EmbedGate>
      </BrowserRouter>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}
